// frontend/src/pages/SupplierPayment/SupplierPayment.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  Truck, Calendar, DollarSign, CreditCard,
  Smartphone, Wallet, FileText, Hash,
  Loader2, X, Plus, Trash2, Save,
  Receipt, Banknote, CircleDollarSign,
  Building, AlertCircle, Layers, CheckCircle,
  BookOpen, ChevronDown, ChevronUp
} from "lucide-react";
import "./SupplierPayment.css";

const url = process.env.REACT_APP_BACKEND_URL;

const SupplierPayment = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();

  // State
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [error, setError] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [showSupplierList, setShowSupplierList] = useState(false);

  // Dropdown data
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [expandedInvoices, setExpandedInvoices] = useState({});

  // Payment methods with account selection
  const [paymentMethods, setPaymentMethods] = useState([
    { method: "cash", account_id: "", account_name: "", amount: "", availableAccounts: [] }
  ]);

  // Payment method options
  const paymentMethodOptions = [
    { value: "cash", label: "Cash", icon: <CreditCard size={16} /> },
    { value: "bank", label: "Bank", icon: <Banknote size={16} /> },
    { value: "mobile_money", label: "Mobile Money", icon: <Smartphone size={16} /> },
    { value: "credit", label: "Credit", icon: <CircleDollarSign size={16} /> }
  ];

  // Map payment method to payment type name for filtering accounts
  const getPaymentTypeName = (method) => {
    const mapping = {
      cash: "Cash",
      bank: "Bank",
      mobile_money: "Mobile Money",
      credit: "Credit"
    };
    return mapping[method] || method;
  };

  const normalizeValue = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ");

  // Supports populated paymentType objects, payment type IDs, and older API shapes.
  const getAccountPaymentTypeName = (account) => {
    const directName =
      account?.paymentType?.paymentstype ||
      account?.paymentType?.name ||
      account?.payment_type?.paymentstype ||
      account?.payment_type?.name ||
      account?.payment_type_name ||
      account?.paymentTypeName ||
      account?.paymentstype;

    if (directName) return normalizeValue(directName);

    const paymentTypeId =
      account?.payment_type_id?._id ||
      account?.payment_type_id ||
      account?.paymentType?._id ||
      (typeof account?.paymentType === "string" ? account.paymentType : "");

    if (paymentTypeId) {
      const matchedType = paymentTypes.find(
        (type) => String(type?._id) === String(paymentTypeId)
      );

      if (matchedType) {
        return normalizeValue(
          matchedType.paymentstype ||
          matchedType.payment_type ||
          matchedType.name
        );
      }
    }

    const accountName = normalizeValue(account?.account_name);

    if (accountName.includes("petty cash") || accountName.includes("cash")) {
      return "cash";
    }

    if (
      accountName.includes("evc") ||
      accountName.includes("mobile") ||
      accountName.includes("wallet")
    ) {
      return "mobile money";
    }

    if (accountName.includes("bank")) {
      return "bank";
    }

    return "";
  };

  const accountMatchesPaymentMethod = (account, method) => {
    const accountPaymentType = getAccountPaymentTypeName(account);
    const selectedMethod = normalizeValue(getPaymentTypeName(method));

    const aliases = {
      cash: ["cash", "cash on hand", "petty cash"],
      bank: ["bank", "bank account"],
      "mobile money": ["mobile money", "mobile", "wallet", "evc"],
      credit: ["credit"]
    };

    return (aliases[selectedMethod] || [selectedMethod]).some(
      (alias) => accountPaymentType === alias || accountPaymentType.includes(alias)
    );
  };

  // Only transaction/posting accounts may be selected for supplier payments.
  const isPostingAccount = (account) => {
    const postingValue = normalizeValue(
      account?.posting_type ||
      account?.postingType ||
      account?.posting ||
      account?.account_posting_type ||
      account?.accountPostingType ||
      account?.account_level_type ||
      account?.accountLevelType
    );

    const accountKind = normalizeValue(
      account?.account_kind ||
      account?.accountKind ||
      account?.node_type ||
      account?.nodeType ||
      account?.category
    );

    const explicitGroup =
      account?.is_group === true ||
      account?.isGroup === true ||
      account?.group_account === true ||
      account?.groupAccount === true ||
      account?.is_parent === true ||
      account?.isParent === true ||
      account?.has_children === true ||
      account?.hasChildren === true;

    const explicitPosting =
      account?.is_posting === true ||
      account?.isPosting === true ||
      account?.allow_posting === true ||
      account?.allowPosting === true ||
      account?.postable === true;

    const isGroupValue =
      ["group", "parent", "control", "header", "summary"].includes(postingValue) ||
      ["group", "parent", "control", "header", "summary"].includes(accountKind);

    const isPostingValue =
      ["posting", "postable", "transaction", "detail", "leaf"].includes(postingValue) ||
      ["posting", "postable", "transaction", "detail", "leaf"].includes(accountKind);

    if (explicitGroup || isGroupValue) return false;
    if (explicitPosting || isPostingValue) return true;

    if (
      account?.locked === true ||
      account?.is_locked === true ||
      account?.isLocked === true
    ) {
      return false;
    }

    const hasParent = Boolean(
      account?.parent_account_id ||
      account?.parentAccountId ||
      account?.parent_id ||
      account?.parentId ||
      account?.parent_account ||
      account?.parentAccount
    );

    if (hasParent) return true;

    return true;
  };

  const getAvailableAccountsForMethod = (method) =>
    accounts.filter(
      (account) =>
        isPostingAccount(account) &&
        accountMatchesPaymentMethod(account, method)
    );

  // Form state
  const [formData, setFormData] = useState({
    supplier_id: "",
    payment_date: new Date().toISOString().split('T')[0],
    reference_no: "",
    notes: ""
  });

  // Totals
  const [totals, setTotals] = useState({
    totalInvoiceAmount: 0,
    totalDueAmount: 0,
    totalPayingNow: 0,
    remainingBalance: 0,
    selectedCount: 0
  });

  // Focus state for floating labels
  const [focusedFields, setFocusedFields] = useState({});

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');

  // Check permissions
  const canCreate = isSystemAdmin || (userType === 'company' && (currentUser?.role === 'admin' || currentUser?.role === 'cashier' || currentUser?.role === 'purchase' || currentUser?.role === 'user'));

  // Get company ID
  const getCompanyId = () => {
    if (userType === 'company' && company) {
      return company._id || company;
    }
    return null;
  };

  // Effect to filter accounts when payment method changes
  useEffect(() => {
    const filterAccountsForPaymentMethod = () => {
      const updatedPayments = [...paymentMethods];

      for (let i = 0; i < updatedPayments.length; i++) {
        const method = updatedPayments[i].method;
        const filtered = getAvailableAccountsForMethod(method);

        updatedPayments[i].availableAccounts = filtered;

        if (updatedPayments[i].account_id) {
          const currentAccount = filtered.find(a => a._id === updatedPayments[i].account_id);
          if (!currentAccount) {
            updatedPayments[i].account_id = "";
            updatedPayments[i].account_name = "";
          }
        }
      }

      setPaymentMethods(updatedPayments);
    };

    if (accounts.length > 0) {
      filterAccountsForPaymentMethod();
    }
  }, [paymentMethods.map(pm => pm.method).join(','), accounts, paymentTypes]);

  // Fetch payment types
  const fetchPaymentTypes = async () => {
    try {
      const companyId = getCompanyId();

      if (!companyId) return;

      const response = await axios.get(`${url}/api/paymenttype/dropdown`, {
        params: { company_id: companyId, include_inactive: false }
      });

      if (response.data.success) {
        setPaymentTypes(response.data.data || []);
      }
    } catch (error) {
      // Silent fail
    }
  };

  // Fetch accounts for payment methods
  const fetchAccounts = async () => {
    try {
      const companyId = getCompanyId();

      if (!companyId) return;

      const response = await axios.get(`${url}/api/account/dropdown`, {
        params: { company_id: companyId, type: "Asset", include_inactive: false }
      });

      if (response.data.success) {
        setAccounts(response.data.data || []);
      }
    } catch (error) {
      // Silent fail
    }
  };

  // Focus handlers for floating labels
  const handleFocus = (field) => {
    setFocusedFields(prev => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field, value) => {
    setFocusedFields(prev => ({ ...prev, [field]: value ? true : false }));
  };

  // Fetch suppliers
  useEffect(() => {
    if (!canCreate) {
      toast.error("You don't have permission");
      navigate("/ViewSupplierPayment");
      return;
    }
    fetchSuppliers();
    fetchAccounts();
    fetchPaymentTypes();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);

      const params = {};
      if (!isSystemAdmin) {
        params.company_id = getCompanyId();
      }

      const response = await axios.get(`${url}/api/supplier/dropdown`, {
        params
      });

      if (response.data.success) {
        setSuppliers(response.data.data || []);
      }
    } catch (error) {
      setError(error.message);
      toast.error("Failed to load suppliers");
    } finally {
      setLoading(false);
    }
  };

  // Fetch available invoices when supplier changes
  useEffect(() => {
    if (formData.supplier_id) {
      setSelectedInvoices([]);
      setAvailableInvoices([]);
      setExpandedInvoices({});
      setPaymentMethods([{ method: "cash", account_id: "", account_name: "", amount: "", availableAccounts: [] }]);
      setTotals({
        totalInvoiceAmount: 0,
        totalDueAmount: 0,
        totalPayingNow: 0,
        remainingBalance: 0,
        selectedCount: 0
      });
      fetchAvailableInvoices();
    } else {
      setAvailableInvoices([]);
      setSelectedInvoices([]);
      setExpandedInvoices({});
      setPaymentMethods([{ method: "cash", account_id: "", account_name: "", amount: "", availableAccounts: [] }]);
      setTotals({
        totalInvoiceAmount: 0,
        totalDueAmount: 0,
        totalPayingNow: 0,
        remainingBalance: 0,
        selectedCount: 0
      });
    }
  }, [formData.supplier_id]);

  const fetchAvailableInvoices = async () => {
    if (!formData.supplier_id) return;

    try {
      setLoadingInvoices(true);

      const params = new URLSearchParams();
      params.append("supplier_id", formData.supplier_id);
      params.append("status", "process");
      params.append("company_id", getCompanyId());
      params.append("_t", Date.now());

      const response = await axios.get(`${url}/api/purchase/invoices?${params.toString()}`);

      if (response.data.success) {
        const allInvoices = response.data.data || [];

        const invoicesWithBalance = allInvoices
          .map((inv) => {
            const total = Number(inv.grand_total || 0);
            const totalPaid = Number(
              inv.total_paid_with_suppliers ?? inv.total_amount_paid ?? 0
            );
            const balance = Number(
              inv.actual_balance ?? Math.max(total - totalPaid, 0)
            );

            return {
              ...inv,
              _id: inv._id,
              invoice_no: inv.invoice_no,
              invoice_date: inv.invoice_date,
              total,
              paid: totalPaid,
              balance,
              paying_now: 0,
              selected: false,
            };
          })
          .filter((inv) => inv.balance > 0.01);

        setAvailableInvoices(invoicesWithBalance);

        const expanded = {};
        invoicesWithBalance.forEach(inv => {
          expanded[inv._id] = false;
        });
        setExpandedInvoices(expanded);
      }
    } catch (error) {
      toast.error("Failed to load purchase invoices");
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Handle invoice selection toggle
  const toggleInvoiceSelection = (invoice) => {
    const isSelected = selectedInvoices.some(inv => inv._id === invoice._id);

    let updatedSelected;
    if (isSelected) {
      updatedSelected = selectedInvoices.filter(inv => inv._id !== invoice._id);
    } else {
      updatedSelected = [...selectedInvoices, { ...invoice, paying_now: 0 }];
    }

    setSelectedInvoices(updatedSelected);
    calculateTotals(updatedSelected);
  };

  // Handle paying now change for a specific invoice
  const handlePayingNowChange = (invoiceId, value) => {
    const amount = value === '' ? 0 : parseFloat(value) || 0;

    const updatedSelected = selectedInvoices.map(inv => {
      if (inv._id === invoiceId) {
        const validAmount = Math.min(amount, inv.balance);
        return { ...inv, paying_now: validAmount };
      }
      return inv;
    });

    setSelectedInvoices(updatedSelected);
    calculateTotals(updatedSelected);
  };

  // Toggle invoice details expansion
  const toggleInvoiceExpand = (invoiceId) => {
    setExpandedInvoices(prev => ({
      ...prev,
      [invoiceId]: !prev[invoiceId]
    }));
  };

  // Calculate totals based on selected invoices
  const calculateTotals = (selected) => {
    const totalInvoiceAmount = selected.reduce((sum, inv) => sum + (inv.total || 0), 0);
    const totalDueAmount = selected.reduce((sum, inv) => sum + (inv.balance || 0), 0);
    const totalPayingNow = selected.reduce((sum, inv) => sum + (inv.paying_now || 0), 0);

    setTotals({
      totalInvoiceAmount,
      totalDueAmount,
      totalPayingNow,
      remainingBalance: totalDueAmount - totalPayingNow,
      selectedCount: selected.length
    });
  };

  // Calculate totals when payment methods change (redistribute across selected invoices)
  useEffect(() => {
    if (selectedInvoices.length === 0) return;

    const totalPayingFromMethods = paymentMethods.reduce((sum, pm) => {
      return sum + (parseFloat(pm.amount) || 0);
    }, 0);

    const totalDue = selectedInvoices.reduce((sum, inv) => sum + (inv.balance || 0), 0);

    const validTotalPaying = Math.min(totalPayingFromMethods, totalDue);

    // ✅ Better warning message with amounts
    if (totalPayingFromMethods > totalDue && totalPayingFromMethods > 0) {
      const excess = Math.round((totalPayingFromMethods - totalDue) * 100) / 100;
      toast.warning(
        `Payment exceeds the total due by $${excess.toFixed(2)}. ` +
        `Total due: $${totalDue.toFixed(2)} • Entered: $${totalPayingFromMethods.toFixed(2)}. ` +
        `Capping at $${totalDue.toFixed(2)}.`
      );
    }

    if (validTotalPaying > 0 && totalDue > 0) {
      const updatedSelected = selectedInvoices.map(inv => {
        const proportion = inv.balance / totalDue;
        const allocatedAmount = validTotalPaying * proportion;
        const roundedAmount = Math.round(allocatedAmount * 100) / 100;

        return {
          ...inv,
          paying_now: Math.min(roundedAmount, inv.balance)
        };
      });

      const totalAllocated = updatedSelected.reduce((sum, inv) => sum + inv.paying_now, 0);
      const difference = validTotalPaying - totalAllocated;

      if (Math.abs(difference) > 0.01) {
        const largestInvoice = [...updatedSelected].sort((a, b) => b.balance - a.balance)[0];
        const index = updatedSelected.findIndex(inv => inv._id === largestInvoice._id);
        if (index !== -1) {
          updatedSelected[index].paying_now = Math.min(
            Math.max(0, updatedSelected[index].paying_now + difference),
            updatedSelected[index].balance
          );
        }
      }

      setSelectedInvoices(updatedSelected);
      calculateTotals(updatedSelected);
    } else {
      const resetSelected = selectedInvoices.map(inv => ({
        ...inv,
        paying_now: 0
      }));
      setSelectedInvoices(resetSelected);
      calculateTotals(resetSelected);
    }
  }, [paymentMethods]);

  // ========== PAYMENT METHOD HANDLERS ==========
  const addPaymentMethod = () => {
    setPaymentMethods([
      ...paymentMethods,
      { method: "cash", account_id: "", account_name: "", amount: "", availableAccounts: [] }
    ]);
  };

  const removePaymentMethod = (index) => {
    if (paymentMethods.length > 1) {
      setPaymentMethods(paymentMethods.filter((_, i) => i !== index));
    } else {
      toast.warning("At least one payment method is required");
    }
  };

  const handlePaymentMethodChange = (index, field, value) => {
    const updated = [...paymentMethods];

    if (field === 'amount') {
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        updated[index][field] = value;
      }
    } else if (field === 'method') {
      updated[index][field] = value;
      updated[index].account_id = "";
      updated[index].account_name = "";

      const filtered = getAvailableAccountsForMethod(value);
      updated[index].availableAccounts = filtered;
    } else if (field === 'account_id') {
      updated[index][field] = value;
      const selectedAccount = updated[index].availableAccounts?.find(a => a._id === value);
      if (selectedAccount) {
        updated[index].account_name = selectedAccount.account_name;
      }
    }

    setPaymentMethods(updated);
  };

  // ========== FORM HANDLERS ==========
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    handleBlur(name, value);
  };

  const validateForm = () => {
    if (!formData.supplier_id) {
      toast.error("Please select a supplier");
      return false;
    }

    if (selectedInvoices.length === 0) {
      toast.error("Please select at least one invoice");
      return false;
    }

    const validPayments = paymentMethods.filter(p =>
      p.amount && parseFloat(p.amount) > 0 && p.account_id
    );

    if (validPayments.length === 0) {
      toast.error("At least one valid payment method with amount and account is required");
      return false;
    }

    if (totals.totalPayingNow <= 0) {
      toast.error("Payment amount must be greater than zero");
      return false;
    }

    // ✅ UPDATED: better overpayment message with amounts
    const rawEntered = paymentMethods.reduce(
      (sum, pm) => sum + (parseFloat(pm.amount) || 0),
      0
    );

    if (rawEntered > totals.totalDueAmount + 0.01) {
      const excess = Math.round((rawEntered - totals.totalDueAmount) * 100) / 100;
      toast.error(
        `Payment exceeds the total due by $${excess.toFixed(2)}. ` +
        `Total due: $${totals.totalDueAmount.toFixed(2)} • ` +
        `Entered: $${rawEntered.toFixed(2)}. ` +
        `Reduce the payment by $${excess.toFixed(2)} to continue.`
      );
      return false;
    }

    if (totals.totalPayingNow > totals.totalDueAmount + 0.01) {
      const excess = Math.round((totals.totalPayingNow - totals.totalDueAmount) * 100) / 100;
      toast.error(
        `Payment exceeds the total due by $${excess.toFixed(2)}. ` +
        `Total due: $${totals.totalDueAmount.toFixed(2)} • ` +
        `Entered: $${totals.totalPayingNow.toFixed(2)}. ` +
        `Reduce the payment by $${excess.toFixed(2)} to continue.`
      );
      return false;
    }

    for (const inv of selectedInvoices) {
      if (inv.paying_now > inv.balance + 0.01) {
        const excess = Math.round((inv.paying_now - inv.balance) * 100) / 100;
        toast.error(
          `Payment for invoice ${inv.invoice_no} exceeds its balance by $${excess.toFixed(2)}. ` +
          `Balance: $${inv.balance.toFixed(2)} • Entered: $${inv.paying_now.toFixed(2)}.`
        );
        return false;
      }
    }

    return true;
  };

  // ========== SUBMIT HANDLER ==========
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const validPaymentMethods = paymentMethods
        .filter(p => p.amount && parseFloat(p.amount) > 0 && p.account_id)
        .map(p => ({
          method: p.method,
          reference_no: formData.reference_no || "-",
          account: accounts.find(a => a._id === p.account_id)?.account_name || p.account_name,
          account_id: p.account_id,
          amount: parseFloat(p.amount)
        }));

      const validDueInvoices = selectedInvoices
        .filter(inv => inv.paying_now > 0)
        .map(inv => ({
          invoice_no: inv.invoice_no,
          invoice_date: inv.invoice_date,
          total: parseFloat(inv.total),
          paid: parseFloat(inv.paid),
          balance: parseFloat(inv.balance),
          paying_now: parseFloat(inv.paying_now)
        }));

      for (const inv of selectedInvoices) {
        if (inv.paying_now <= 0) continue;

        const paymentMethodsForInvoice = validPaymentMethods.map(pm => ({
          ...pm,
          amount: pm.amount * (inv.paying_now / totals.totalPayingNow)
        }));

        const payload = {
          supplier_id: formData.supplier_id,
          purchase_invoice_id: inv._id,
          payment_date: formData.payment_date,
          payment_methods: paymentMethodsForInvoice,
          due_invoices: [{
            invoice_no: inv.invoice_no,
            invoice_date: inv.invoice_date,
            total: parseFloat(inv.total),
            paid: parseFloat(inv.paid),
            balance: parseFloat(inv.balance),
            paying_now: parseFloat(inv.paying_now)
          }],
          total_paying_now: parseFloat(inv.paying_now),
          company_id: getCompanyId(),
          notes: formData.notes || `Payment for invoice ${inv.invoice_no}`,
          status: "process"
        };

        await axios.post(`${url}/api/supplierpayment`, payload, {
          headers: {
            "Content-Type": "application/json"
          }
        });
      }

      toast.success(`${selectedInvoices.length} supplier payment(s) created successfully!`);

      if (formData.supplier_id) {
        await fetchAvailableInvoices();
      }

      setTimeout(() => {
        navigate("/ViewSupplierPayment");
      }, 1500);

    } catch (error) {
      if (error.response) {
        toast.error(error.response.data?.message || "Failed to create supplier payment");
      } else {
        toast.error("Network error. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate("/ViewSupplierPayment");
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return "Invalid date";
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: 'Cash',
      bank: 'Bank',
      mobile_money: 'Mobile Money',
      credit: 'Credit'
    };
    return labels[method] || method;
  };

  // ✅ Overpayment detection — compares raw entered amount to total due
  const rawPaymentTotal = paymentMethods.reduce(
    (sum, pm) => sum + (parseFloat(pm.amount) || 0),
    0
  );
  const overpaidAmount = Math.max(
    0,
    rawPaymentTotal - totals.totalDueAmount
  );
  const hasOverpayment =
    overpaidAmount > 0.01 && selectedInvoices.length > 0;

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="branch-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading supplier payment form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-wrapper">
        <div className="branch-form-container">
          <div className="error-container">
            <AlertCircle size={48} color="#dc3545" />
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button onClick={handleCancel} className="cancel-btn">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="branch-form-container">
        <div className="form-header">
          <h2 className="form-title">
            <Receipt size={24} />
            Supplier Payment
          </h2>
        </div>

        {/* Company Banner */}
        <div className="company-banner">
          <Building size={18} />
          <span>
            {isSystemAdmin ? 'System Admin' : company?.company_name} - Creating payment for supplier invoices
          </span>
        </div>

        {formData.supplier_id && !loadingInvoices && availableInvoices.length === 0 && (
          <div className="company-banner" style={{ background: '#fff2f0', borderLeftColor: '#ff4d4f' }}>
            <AlertCircle size={18} color="#ff4d4f" />
            <span>No unpaid invoices found for this supplier. All invoices are fully paid.</span>
          </div>
        )}

        <form className="branch-form" onSubmit={handleSubmit}>
          {/* Payment Information Section */}
          <div className="section-title">
            <DollarSign size={18} />
            Payment Information
          </div>

          <div className="form-row">
            {/* Payment No - Auto-generated */}
            <div className={`form-field ${focusedFields.payment_no ? 'filled' : ''}`}>
              <Hash className="field-icon" size={18} />
              <input
                type="text"
                value="Auto-generated"
                disabled
                onFocus={() => handleFocus('payment_no')}
              />
              <label>Payment No</label>
              <span className="field-hint">Will be auto-generated</span>
            </div>

            {/* Reference No */}
            <div className={`form-field ${focusedFields.reference_no || formData.reference_no ? 'filled' : ''}`}>
              <BookOpen className="field-icon" size={18} />
              <input
                type="text"
                name="reference_no"
                value={formData.reference_no}
                onChange={handleChange}
                onFocus={() => handleFocus('reference_no')}
                onBlur={(e) => handleBlur('reference_no', e.target.value)}
                placeholder=" "
              />
              <label>Reference No</label>
              <span className="field-hint">Optional reference for all payment methods</span>
            </div>

            {/* Payment Date */}
            <div className={`form-field ${focusedFields.payment_date || formData.payment_date ? 'filled' : ''}`}>
              <Calendar className="field-icon" size={18} />
              <input
                type="date"
                name="payment_date"
                value={formData.payment_date}
                onChange={handleChange}
                onFocus={() => handleFocus('payment_date')}
                onBlur={(e) => handleBlur('payment_date', e.target.value)}
                required
              />
              <label>Payment Date *</label>
            </div>
          </div>

          <div className="form-row">
            {/* Supplier */}
            <div
              className={`form-field ${
                focusedFields.supplier_id || formData.supplier_id ? "filled" : ""
              }`}
              style={{ gridColumn: "span 2", position: "relative" }}
            >
              <Truck className="field-icon" size={18} />

              <input
                type="text"
                name="supplier_search"
                value={supplierSearch}
                autoComplete="off"
                placeholder=" "
                onChange={(e) => {
                  const value = e.target.value;

                  setSupplierSearch(value);
                  setShowSupplierList(true);

                  setFormData((prev) => ({
                    ...prev,
                    supplier_id: "",
                  }));
                }}
                onFocus={() => {
                  handleFocus("supplier_id");
                  setShowSupplierList(true);
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowSupplierList(false);
                  }, 200);

                  handleBlur("supplier_id", formData.supplier_id);
                }}
                required
                disabled={loading}
              />

              <label>Supplier *</label>

              {showSupplierList && (
                <div className="supplier-dropdown-list">
                  {suppliers
                    .filter((supplier) => {
                      const supplierNumber = String(
                        supplier?.supplier_id ?? ""
                      );

                      const supplierName = String(
                        supplier?.supplier_name ?? ""
                      );

                      const searchValue =
                        `${supplierNumber} ${supplierName}`.toLowerCase();

                      return searchValue.includes(
                        supplierSearch.trim().toLowerCase()
                      );
                    })
                    .map((supplier) => {
                      const supplierNumber =
                        supplier?.supplier_id !== undefined &&
                        supplier?.supplier_id !== null
                          ? String(supplier.supplier_id)
                          : "";

                      const supplierName =
                        supplier?.supplier_name || "Unnamed Supplier";

                      const supplierLabel = supplierNumber
                        ? `${supplierNumber} - ${supplierName}`
                        : supplierName;

                      return (
                        <div
                          key={supplier._id}
                          className="supplier-dropdown-item"
                          onMouseDown={(e) => {
                            e.preventDefault();

                            setFormData((prev) => ({
                              ...prev,
                              supplier_id: supplier._id,
                            }));

                            setSupplierSearch(supplierLabel);

                            setShowSupplierList(false);
                          }}
                        >
                          {supplierLabel}
                        </div>
                      );
                    })}

                  {suppliers.filter((supplier) => {
                    const supplierNumber = String(
                      supplier?.supplier_id ?? ""
                    );

                    const supplierName = String(
                      supplier?.supplier_name ?? ""
                    );

                    return `${supplierNumber} ${supplierName}`
                      .toLowerCase()
                      .includes(supplierSearch.trim().toLowerCase());
                  }).length === 0 && (
                    <div className="supplier-dropdown-item">
                      No supplier found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Notes */}
            <div
              className={`form-field ${
                focusedFields.notes || formData.notes ? "filled" : ""
              }`}
            >
              <FileText className="field-icon" size={18} />

              <input
                type="text"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                onFocus={() => handleFocus("notes")}
                onBlur={(e) => handleBlur("notes", e.target.value)}
                placeholder=" "
              />

              <label>Notes</label>
            </div>
          </div>

          {/* Available Invoices Section */}
          <div className="section-title">
            <Layers size={18} />
            Available Invoices {loadingInvoices && <Loader2 className="animate-spin" size={14} style={{ marginLeft: '8px' }} />}
          </div>

          {formData.supplier_id && availableInvoices.length > 0 && (
            <div className="invoices-list" style={{ marginBottom: '20px' }}>
              {availableInvoices.map(invoice => {
                const isSelected = selectedInvoices.some(inv => inv._id === invoice._id);
                const selectedInvoice = selectedInvoices.find(inv => inv._id === invoice._id);
                const isExpanded = expandedInvoices[invoice._id];

                return (
                  <div
                    key={invoice._id}
                    className={`invoice-card ${isSelected ? 'selected' : ''}`}
                    style={{
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      padding: '12px',
                      backgroundColor: isSelected ? '#f6ffed' : '#fff',
                      borderLeft: isSelected ? '4px solid #52c41a' : '1px solid #d9d9d9'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleInvoiceSelection(invoice)}
                        style={{ width: '18px', height: '18px' }}
                      />

                      <div style={{ flex: 2 }}>
                        <strong>{invoice.invoice_no}</strong>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Date: {formatDate(invoice.invoice_date)}
                        </div>
                      </div>

                      <div style={{ flex: 1, textAlign: 'right' }}>
                        <div>Total: {formatCurrency(invoice.total)}</div>
                        <div style={{ color: '#52c41a' }}>Paid: {formatCurrency(invoice.paid)}</div>
                      </div>

                      <div style={{ flex: 1, textAlign: 'right' }}>
                        <div style={{ color: '#ff4d4f', fontWeight: 600 }}>
                          Balance: {formatCurrency(invoice.balance)}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => toggleInvoiceExpand(invoice._id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                    </div>

                    {isExpanded && isSelected && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed #d9d9d9' }}>
                        <div className="form-row" style={{ marginBottom: 0 }}>
                          <div className="form-field" style={{ marginBottom: 0 }}>
                            <DollarSign className="field-icon" size={18} />
                            <input
                              type="number"
                              value={selectedInvoice?.paying_now || ''}
                              onChange={(e) => handlePayingNowChange(invoice._id, e.target.value)}
                              placeholder=" "
                              min="0"
                              max={invoice.balance}
                              step="0.01"
                              disabled={!isSelected}
                              style={{ paddingLeft: '2.5rem' }}
                            />
                            <label>Paying Now</label>
                            <span className="field-hint">Max: {formatCurrency(invoice.balance)}</span>
                          </div>

                          {selectedInvoice && selectedInvoice.paying_now > 0 && (
                            <div style={{
                              marginLeft: '12px',
                              padding: '8px',
                              background: '#e6f7ff',
                              borderRadius: '4px',
                              fontSize: '13px',
                              minWidth: '150px'
                            }}>
                              <div>After payment:</div>
                              <div style={{ fontWeight: 600, color: '#1890ff' }}>
                                {formatCurrency(invoice.balance - selectedInvoice.paying_now)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {formData.supplier_id && !loadingInvoices && availableInvoices.length === 0 && (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              background: '#fafafa',
              borderRadius: '4px',
              marginBottom: '20px',
              color: '#999'
            }}>
              <AlertCircle size={24} color="#999" />
              <p>No unpaid invoices available for this supplier</p>
            </div>
          )}

          {/* Selected Invoices Summary */}
          {selectedInvoices.length > 0 && (
            <div className="total-cost-display" style={{ marginBottom: '20px' }}>
              <div className="total-label">
                <Layers size={18} />
                Selected Invoices Summary ({totals.selectedCount})
              </div>
              <div className="total-grid">
                <div className="total-item">
                  <span className="total-label">Total Invoice Amount:</span>
                  <span className="total-value">{formatCurrency(totals.totalInvoiceAmount)}</span>
                </div>
                <div className="total-item">
                  <span className="total-label">Total Previously Paid:</span>
                  <span className="total-value" style={{ color: '#52c41a' }}>
                    {formatCurrency(totals.totalInvoiceAmount - totals.totalDueAmount)}
                  </span>
                </div>
                <div className="total-item">
                  <span className="total-label">Total Current Balance:</span>
                  <span className="total-value" style={{ color: '#ff4d4f', fontWeight: 600 }}>
                    {formatCurrency(totals.totalDueAmount)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Payment Methods Section */}
          <div className="section-title">
            <CreditCard size={18} />
            Payment Methods
          </div>

          {paymentMethods.map((payment, index) => (
            <div key={index} className="payment-method-row" style={{ marginBottom: '15px' }}>
              <div className="form-row">
                {/* Method */}
                <div className={`form-field`} style={{ flex: 2 }}>
                  <select
                    value={payment.method}
                    onChange={(e) => handlePaymentMethodChange(index, 'method', e.target.value)}
                    style={{ paddingLeft: '2.5rem' }}
                  >
                    {paymentMethodOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className="field-icon">
                    {payment.method === 'cash' && <CreditCard size={18} />}
                    {payment.method === 'bank' && <Banknote size={18} />}
                    {payment.method === 'mobile_money' && <Smartphone size={18} />}
                    {payment.method === 'credit' && <CircleDollarSign size={18} />}
                  </span>
                  <label>Method *</label>
                </div>

                {/* Account Selection with Dropdown - Filtered by Payment Type */}
                <div className={`form-field`} style={{ flex: 3 }}>
                  <Building className="field-icon" size={18} />
                  <select
                    value={payment.account_id || ""}
                    onChange={(e) => handlePaymentMethodChange(index, 'account_id', e.target.value)}
                    required
                    disabled={!payment.availableAccounts || payment.availableAccounts.length === 0}
                  >
                    <option value="">Select Account</option>
                    {(payment.availableAccounts || []).map(acc => (
                      <option key={acc._id} value={acc._id}>
                        {acc.account_name}
                        {acc.paymentType && ` (${acc.paymentType.paymentstype})`}
                        {` - Balance: ${formatCurrency(acc.balance || 0)}`}
                      </option>
                    ))}
                  </select>
                  <label>Account *</label>
                  {(!payment.availableAccounts || payment.availableAccounts.length === 0) && (
                    <span className="field-hint" style={{ color: '#ff4d4f' }}>
                      No accounts available for {getPaymentMethodLabel(payment.method)}. Please add an account with this payment type.
                    </span>
                  )}
                </div>

                {/* Amount */}
                <div className={`form-field`} style={{ flex: 2 }}>
                  <DollarSign className="field-icon" size={18} />
                  <input
                    type="number"
                    value={payment.amount}
                    onChange={(e) => handlePaymentMethodChange(index, 'amount', e.target.value)}
                    placeholder=" "
                    min="0"
                    step="0.01"
                    required
                  />
                  <label>Amount *</label>
                </div>

                {/* Remove button */}
                {paymentMethods.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePaymentMethod(index)}
                    className="delete-btn"
                    style={{
                      width: '40px',
                      height: '40px',
                      marginTop: '8px',
                      flex: '0 0 40px'
                    }}
                    title="Remove Payment Method"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addPaymentMethod}
            className="add-button"
            style={{ marginBottom: '20px', alignSelf: 'flex-start' }}
            disabled={selectedInvoices.length === 0}
          >
            <Plus size={16} /> Add Payment Method
          </button>

          {/* Totals Summary */}
          <div className="total-cost-display">
            <div className="total-label">
              <DollarSign size={18} />
              Payment Summary
            </div>
            <div className="total-grid">
              <div className="total-item">
                <span className="total-label">Total Invoice Amount:</span>
                <span className="total-value">{formatCurrency(totals.totalInvoiceAmount)}</span>
              </div>
              <div className="total-item">
                <span className="total-label">Total Previously Paid:</span>
                <span className="total-value" style={{ color: '#52c41a' }}>
                  {formatCurrency(totals.totalInvoiceAmount - totals.totalDueAmount)}
                </span>
              </div>
              <div className="total-item">
                <span className="total-label">Total Current Balance:</span>
                <span className="total-value" style={{ color: '#ff4d4f', fontWeight: 600 }}>
                  {formatCurrency(totals.totalDueAmount)}
                </span>
              </div>
              <div className="total-item">
                <span className="total-label">Total Paying Now:</span>
                <span className="total-value" style={{ color: '#1890ff', fontWeight: 600 }}>
                  {formatCurrency(totals.totalPayingNow)}
                </span>
              </div>
              <div className="total-item grand-total">
                <span className="total-label">Remaining Balance After Payment:</span>
                <span className="total-value" style={{
                  color: totals.remainingBalance > 0 ? '#ff4d4f' : '#52c41a',
                  fontWeight: 700
                }}>
                  {formatCurrency(totals.remainingBalance)}
                </span>
              </div>
            </div>
          </div>

          {/* ✅ Overpayment warning — shown above the buttons */}
          {hasOverpayment && (
            <div
              style={{
                marginBottom: 15,
                padding: '12px 16px',
                background: '#fff1f0',
                border: '1px solid #ffa39e',
                borderLeft: '4px solid #f5222d',
                borderRadius: 4,
                color: '#cf1322',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <AlertCircle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                <strong style={{ display: 'block', marginBottom: 4 }}>
                  Payment amount cannot exceed the total due.
                </strong>
                <div style={{ marginBottom: 4 }}>
                  Total Due: <strong>{formatCurrency(totals.totalDueAmount)}</strong>
                  {" • "}
                  Entered: <strong>{formatCurrency(rawPaymentTotal)}</strong>
                  {" • "}
                  Excess: <strong style={{ color: '#f5222d' }}>{formatCurrency(overpaidAmount)}</strong>
                </div>
                <div>
                  Reduce the payment by{" "}
                  <strong>{formatCurrency(overpaidAmount)}</strong> to continue.
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="button-group">
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              className="cancel-btn"
            >
              <X size={16} /> Cancel
            </button>

            <button
              type="submit"
              disabled={
                submitting ||
                hasOverpayment ||
                selectedInvoices.length === 0 ||
                totals.totalDueAmount <= 0
              }
              className="submit-btn"
              title={
                hasOverpayment
                  ? `Payment exceeds total due by ${formatCurrency(overpaidAmount)}`
                  : ""
              }
              style={
                hasOverpayment
                  ? { backgroundColor: '#d9d9d9', cursor: 'not-allowed' }
                  : undefined
              }
            >
              {submitting ? (
                <>
                  <div className="spinner"></div>
                  Creating {selectedInvoices.length} Payment(s)...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Create {selectedInvoices.length > 1 ? `${selectedInvoices.length} Payments` : 'Payment'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplierPayment;